import { useState } from "react";
import { Check, X, Trash2, Star, Pencil, Plus, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { MultiImageUpload, SingleImageUpload } from "@/components/ImageUpload";
import { img, fmtDate } from "@/lib/community";
import { useAdminData, adminAct, Field, NSelect, inputCls, Badge, Panel, btnDark, btnLine, btnDanger } from "./shared";

const CATS = ["", "Pilates & Wellness", "Girls' Brunch", "Sip & Paint", "Photoshoot", "Beauty & Self-Care", "Faith & Bible Study",
  "Networking", "Travel Experience", "Community Gathering"];
const VIS = [{ value: "public", label: "Public" }, { value: "members", label: "Jenova Girls" }, { value: "prive", label: "Privé" }];

function PhotoEditor({ photo, albums, onDone }) {
  const [f, setF] = useState({ caption: photo.caption || "", album_id: photo.album_id || "", category: photo.category || "", date: photo.date || "",
    featured: !!photo.featured, visibility: photo.visibility || "public", sort_order: photo.sort_order || 0 });
  const save = () => adminAct(() => api.put(`/admin/community/photos/${photo.id}`, { ...f, album_id: f.album_id || null, category: f.category || null, sort_order: Number(f.sort_order) }), "Saved").then(onDone);
  return (
    <div className="space-y-4" data-testid="photo-editor">
      <img src={img(photo.url)} alt="" className="w-full max-h-72 object-contain bg-[#F5EFE6] rounded" />
      {photo.submitted_email && <p className="text-xs text-[#7A6A62]">Submitted by {photo.submitted_name} ({photo.submitted_email}) · consent given {fmtDate(photo.consent?.at)} · {photo.consent?.allow_public ? "public OK" : "members only"}</p>}
      <Field label="Caption"><input className={inputCls} value={f.caption} onChange={(e) => setF({ ...f, caption: e.target.value })} data-testid="photo-caption-input" /></Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Album"><NSelect value={f.album_id} onChange={(v) => setF({ ...f, album_id: v })} options={[{ value: "", label: "No album" }, ...albums.map((a) => ({ value: a.id, label: a.title }))]} testId="photo-album-select" /></Field>
        <Field label="Category"><NSelect value={f.category} onChange={(v) => setF({ ...f, category: v })} options={CATS.map((c) => ({ value: c, label: c || "None" }))} testId="photo-category-select" /></Field>
        <Field label="Date"><input type="date" className={inputCls} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
        <Field label="Who can see it"><NSelect value={f.visibility} onChange={(v) => setF({ ...f, visibility: v })} options={VIS} testId="photo-visibility-select" /></Field>
        <Field label="Order (lower first)"><input type="number" className={inputCls} value={f.sort_order} onChange={(e) => setF({ ...f, sort_order: e.target.value })} /></Field>
      </div>
      <label className="flex items-center gap-2 text-sm"><Switch checked={f.featured} onCheckedChange={(v) => setF({ ...f, featured: v })} data-testid="photo-featured-switch" /> Featured (also shows on The Experience page)</label>
      <div className="flex justify-end"><button className={btnDark} onClick={save} data-testid="photo-save-btn">Save</button></div>
    </div>
  );
}

function AlbumEditor({ album, onDone }) {
  const [f, setF] = useState({ title: "", description: "", category: "", date: "", cover_image: "", visibility: "public", ...album });
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    const saved = await adminAct(() => (album.id ? api.put(`/admin/community/albums/${album.id}`, f) : api.post("/admin/community/albums", f)));
    if (saved) {
      for (const url of photos) {
        await adminAct(() => api.post("/admin/community/photos", { url, album_id: saved.id, category: f.category || null, visibility: f.visibility, status: "approved" }));
      }
      toast.success(photos.length ? `Album saved with ${photos.length} new photo${photos.length > 1 ? "s" : ""}` : "Album saved");
      onDone();
    }
    setBusy(false);
  };
  return (
    <div className="space-y-4" data-testid="album-editor">
      <Field label="Album title"><input className={inputCls} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="album-title-input" /></Field>
      <Field label="Description"><input className={inputCls} value={f.description || ""} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
      <div className="grid grid-cols-3 gap-4">
        <Field label="Category"><NSelect value={f.category || ""} onChange={(v) => setF({ ...f, category: v })} options={CATS.map((c) => ({ value: c, label: c || "None" }))} /></Field>
        <Field label="Date"><input type="date" className={inputCls} value={f.date || ""} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
        <Field label="Visibility"><NSelect value={f.visibility} onChange={(v) => setF({ ...f, visibility: v })} options={VIS} /></Field>
      </div>
      <Field label="Cover"><SingleImageUpload value={f.cover_image} onChange={(v) => setF({ ...f, cover_image: v })} testId="album-cover" aspectRatio="aspect-[4/3]" /></Field>
      <Field label={album.id ? "Add more photos (select as many as you like)" : "Photos (select as many as you like)"}>
        <MultiImageUpload value={photos} onChange={setPhotos} testId="album-photos" />
      </Field>
      <div className="flex justify-end"><button className={btnDark} onClick={save} disabled={busy || !f.title} data-testid="album-save-btn">{busy ? "Saving…" : "Save album"}</button></div>
    </div>
  );
}

export default function GalleryTab() {
  const [data, reload] = useAdminData("/admin/community/gallery");
  const [uploadAlbum, setUploadAlbum] = useState("");
  const [editPhoto, setEditPhoto] = useState(null);
  const [editAlbum, setEditAlbum] = useState(null);
  if (!data) return null;
  const pending = data.photos.filter((p) => p.status === "pending");
  const live = data.photos.filter((p) => p.status !== "pending");
  const setStatus = (p, status) => adminAct(() => api.put(`/admin/community/photos/${p.id}`, { status }), status === "approved" ? "Published to the wall" : "Rejected").then(reload);
  const toggleFeat = (p) => adminAct(() => api.put(`/admin/community/photos/${p.id}`, { featured: !p.featured })).then(reload);
  const del = (p) => window.confirm("Delete this photo?") && adminAct(() => api.delete(`/admin/community/photos/${p.id}`), "Deleted").then(reload);
  const delAlbum = (a) => window.confirm(`Delete album "${a.title}"? Photos stay on the wall.`) && adminAct(() => api.delete(`/admin/community/albums/${a.id}`), "Album deleted").then(reload);
  const emailAlbum = async (a) => {
    if (!window.confirm(a.emailed_at ? `This album was already emailed on ${fmtDate(a.emailed_at)}. Send it again to everyone?` : `Email "${a.title}" with its photos to all Jenova Girls${a.event_id ? " and the experience's guests" : ""}?`)) return;
    const r = await adminAct(() => api.post(`/admin/community/albums/${a.id}/email`));
    if (r) { window.alert(`Sending to ${r.recipients} women.`); reload(); }
  };
  const addPhotos = async (urls) => {
    for (const url of urls) await adminAct(() => api.post("/admin/community/photos", { url, album_id: uploadAlbum || null, status: "approved" }));
    reload();
  };

  const tile = (p, actions) => (
    <div key={p.id} className="border border-[#E8DCCB] rounded-lg overflow-hidden" data-testid={`gallery-photo-${p.id}`}>
      <img src={img(p.url)} alt="" className="w-full aspect-square object-cover" />
      <div className="p-2.5 space-y-2">
        <div className="flex items-center justify-between gap-2"><Badge s={p.status} /><span className="text-[10px] text-[#7A6A62]">{p.visibility}</span></div>
        {p.caption && <p className="text-xs line-clamp-2">{p.caption}</p>}
        <div className="flex flex-wrap gap-1">{actions}</div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-gallery-tab">
      <Panel title={`Photo approvals (${pending.length})`} testId="gallery-pending">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {pending.map((p) => tile(p, <>
            <button className={btnDark} onClick={() => setStatus(p, "approved")} data-testid={`photo-approve-${p.id}`}><Check className="w-3 h-3" /></button>
            <button className={btnDanger} onClick={() => setStatus(p, "rejected")} data-testid={`photo-reject-${p.id}`}><X className="w-3 h-3" /></button>
            <button className={btnLine} onClick={() => setEditPhoto(p)} data-testid={`photo-edit-${p.id}`}><Pencil className="w-3 h-3" /></button>
          </>))}
        </div>
        {!pending.length && <p className="text-sm text-[#7A6A62]">No submissions waiting. Member photos are never published until you approve them.</p>}
      </Panel>
      <Panel title="Albums" action={<button className={btnDark} onClick={() => setEditAlbum({})} data-testid="album-new-btn"><Plus className="w-3.5 h-3.5" /> New album</button>}>
        <div className="flex flex-wrap gap-2">
          {data.albums.map((a) => (
            <span key={a.id} className="inline-flex items-center gap-2 border border-[#E8DCCB] rounded-full pl-4 pr-1.5 py-1 text-sm" data-testid={`album-chip-${a.id}`}>
              {a.title} {a.auto && <span className="text-[10px] text-[#A8865A]">auto</span>} <span className="text-[10px] text-[#7A6A62]">{fmtDate(a.date, { month: "short", year: "numeric" })}</span>
              <button onClick={() => emailAlbum(a)} className="p-1" title={a.emailed_at ? `Emailed ${fmtDate(a.emailed_at)}` : "Email this album to everyone"} data-testid={`album-email-${a.id}`}><Mail className={`w-3 h-3 ${a.emailed_at ? "text-[#A8865A]" : ""}`} /></button>
              <button onClick={() => setEditAlbum(a)} className="p-1" data-testid={`album-edit-${a.id}`}><Pencil className="w-3 h-3" /></button>
              <button onClick={() => delAlbum(a)} className="p-1 text-rose-600" data-testid={`album-delete-${a.id}`}><Trash2 className="w-3 h-3" /></button>
            </span>
          ))}
          {!data.albums.length && <p className="text-sm text-[#7A6A62]">Create albums for each experience (e.g. “Spring Pilates Brunch”).</p>}
        </div>
      </Panel>
      <Panel title="Upload to the wall">
        <div className="max-w-xs mb-4"><Field label="Add to album"><NSelect value={uploadAlbum} onChange={setUploadAlbum} options={[{ value: "", label: "No album" }, ...data.albums.map((a) => ({ value: a.id, label: a.title }))]} testId="gallery-upload-album" /></Field></div>
        <MultiImageUpload value={[]} onChange={addPhotos} testId="gallery-upload" />
      </Panel>
      <Panel title={`On the wall & archived (${live.length})`}>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {live.map((p) => tile(p, <>
            <button className={btnLine} onClick={() => toggleFeat(p)} data-testid={`photo-feature-${p.id}`}><Star className={`w-3 h-3 ${p.featured ? "fill-current text-[#A8865A]" : ""}`} /></button>
            <button className={btnLine} onClick={() => setEditPhoto(p)} data-testid={`photo-edit-${p.id}`}><Pencil className="w-3 h-3" /></button>
            {p.status === "rejected" && <button className={btnLine} onClick={() => setStatus(p, "approved")}><Check className="w-3 h-3" /></button>}
            <button className={btnDanger} onClick={() => del(p)} data-testid={`photo-delete-${p.id}`}><Trash2 className="w-3 h-3" /></button>
          </>))}
        </div>
      </Panel>
      <Dialog open={!!editPhoto} onOpenChange={(o) => !o && setEditPhoto(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogTitle className="font-serif text-2xl font-light">Edit photo</DialogTitle>
          {editPhoto && <PhotoEditor photo={editPhoto} albums={data.albums} onDone={() => { setEditPhoto(null); reload(); }} />}
        </DialogContent>
      </Dialog>
      <Dialog open={!!editAlbum} onOpenChange={(o) => !o && setEditAlbum(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogTitle className="font-serif text-2xl font-light">{editAlbum?.id ? "Edit album" : "New album"}</DialogTitle>
          {editAlbum && <AlbumEditor album={editAlbum} onDone={() => { setEditAlbum(null); reload(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
